// Facturacion y rentabilidad: ranking, margen por producto y mix por categoria.
// Recibe todo ya calculado: cargar y agregar es cosa de la pantalla.

import React from 'react';
import { BarChart3, Layers, LayoutList, Percent, Tag } from 'lucide-react';
import { Bar, BarChart, CartesianGrid, Cell, Legend, Pie, PieChart, Tooltip, XAxis, YAxis } from 'recharts';
import Chart from '../../components/Chart';
import { PIE_COLORS, formatInt, formatMoney, formatPercent, margenColorClass } from './formato';
import { DataTable, EmptyState, SectionCard } from './componentes';

export default function TabRentabilidad({
  productosVendidosPorUnidad,
  rentabilidadProductos,
  setShowPromoProducts,
  setViewCategorias,
  setViewTopProductos,
  setViewUnidades,
  showPromoProducts,
  ventasPorCategoria,
  viewCategorias,
  viewTopProductos,
  viewUnidades,
}) {
  return (
            <>
              {/* Ranking de más vendidos (con toggle Promo + columna margen) */}
              <SectionCard
                title="Ranking de más vendidos"
                icon={BarChart3}
                action={
                  <div className="flex items-center gap-2">
                    {productosVendidosPorUnidad.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setViewUnidades((v) => (v === 'chart' ? 'table' : 'chart'))}
                        title={viewUnidades === 'chart' ? 'Ver tabla' : 'Ver gráfico'}
                        className={`inline-flex h-7 items-center gap-1 rounded-lg border px-2 text-[10px] font-black uppercase tracking-widest transition-colors ${
                          viewUnidades === 'table' ? 'border-blue-200 bg-blue-50 text-blue-600' : 'border-slate-200 bg-white text-slate-400 hover:text-slate-700'
                        }`}
                      >
                        {viewUnidades === 'chart' ? <LayoutList size={12} /> : <BarChart3 size={12} />}
                        {viewUnidades === 'chart' ? 'Tabla' : 'Gráfico'}
                      </button>
                    )}
                    <button
                      type="button"
                      aria-pressed={showPromoProducts}
                      title={showPromoProducts ? 'Mostrando promos como unidad (no cuenta los productos del combo)' : 'Mostrando los productos del combo por separado'}
                      onClick={() => setShowPromoProducts((value) => !value)}
                      className={`inline-flex h-7 items-center justify-center gap-1 rounded-lg border px-2 text-[10px] font-black uppercase tracking-widest transition-colors ${
                        showPromoProducts ? 'border-fuchsia-200 bg-fuchsia-50 text-fuchsia-700' : 'border-slate-200 bg-white text-slate-400 hover:bg-slate-50 hover:text-slate-600'
                      }`}
                    >
                      <Tag size={13} />
                      <span>Promo</span>
                    </button>
                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                      {productosVendidosPorUnidad.length} prod.
                    </span>
                  </div>
                }
              >
                {productosVendidosPorUnidad.length === 0 ? (
                  <EmptyState message="Sin productos vendidos" />
                ) : viewUnidades === 'chart' ? (
                  <div style={{ width: '100%', height: Math.max(240, Math.min(productosVendidosPorUnidad.length, 15) * 30), minWidth: 0 }}>
                    <Chart>
                      <BarChart
                        data={[...productosVendidosPorUnidad].slice(0, 15).reverse().map((p) => ({
                          name: p.nombre.length > 20 ? p.nombre.slice(0, 20) + '…' : p.nombre,
                          unidades: p.cant,
                          venta: p.venta,
                        }))}
                        layout="vertical"
                        margin={{ top: 0, right: 16, bottom: 0, left: 4 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
                        <XAxis type="number" stroke="#94A3B8" fontSize={10} />
                        <YAxis type="category" dataKey="name" stroke="#94A3B8" fontSize={10} width={130} tick={{ fill: '#475569' }} />
                        <Tooltip
                          formatter={(value, name) => [name === 'venta' ? `$ ${formatMoney(value)}` : formatInt(value), name === 'venta' ? 'Venta' : 'Unidades']}
                          contentStyle={{ borderRadius: 12, border: '1px solid #E2E8F0', fontSize: 12 }}
                        />
                        <Legend iconSize={8} formatter={(v) => <span style={{ fontSize: 10, color: '#475569' }}>{v === 'unidades' ? 'Unidades' : 'Venta $'}</span>} />
                        <Bar dataKey="unidades" fill="#FFB454" radius={[0, 4, 4, 0]} maxBarSize={18} />
                        <Bar dataKey="venta" fill="#51A7FD" radius={[0, 4, 4, 0]} maxBarSize={18} />
                      </BarChart>
                    </Chart>
                  </div>
                ) : (
                  <DataTable
                    emptyMessage="Sin productos vendidos"
                    rows={productosVendidosPorUnidad}
                    columns={[
                      {
                        key: 'nombre',
                        label: 'Producto',
                        cellClass: () => 'font-black text-slate-800',
                        render: (p) => (
                          <div className="flex items-center gap-2 min-w-0">
                            {p.isPromo && (
                              <span className="shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-fuchsia-100 text-fuchsia-700 text-[9px] font-black uppercase tracking-widest">
                                <Tag size={9} /> Promo
                              </span>
                            )}
                            <span className="truncate max-w-[220px]">{p.nombre}</span>
                          </div>
                        ),
                      },
                      { key: 'cant', label: 'Unid.', align: 'right', cellClass: () => 'font-bold text-slate-600', render: (p) => formatInt(p.cant) },
                      { key: 'venta', label: 'Venta', align: 'right', cellClass: () => 'font-black text-slate-900', render: (p) => `$ ${formatMoney(p.venta)}` },
                      { key: 'margenPct', label: 'Margen', align: 'right', cellClass: (p) => `font-black ${margenColorClass(p.margenPct)}`, render: (p) => formatPercent(p.margenPct) },
                    ]}
                  />
                )}
              </SectionCard>

              {/* Rentabilidad por producto */}
              <SectionCard
                title="Rentabilidad por producto"
                icon={Percent}
                action={
                  rentabilidadProductos.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setViewTopProductos((v) => (v === 'chart' ? 'table' : 'chart'))}
                      title={viewTopProductos === 'chart' ? 'Ver tabla' : 'Ver gráfico'}
                      className={`inline-flex h-7 items-center gap-1 rounded-lg border px-2 text-[10px] font-black uppercase tracking-widest transition-colors ${
                        viewTopProductos === 'table' ? 'border-blue-200 bg-blue-50 text-blue-600' : 'border-slate-200 bg-white text-slate-400 hover:text-slate-700'
                      }`}
                    >
                      {viewTopProductos === 'chart' ? <LayoutList size={12} /> : <BarChart3 size={12} />}
                      {viewTopProductos === 'chart' ? 'Tabla' : 'Gráfico'}
                    </button>
                  )
                }
              >
                {rentabilidadProductos.length === 0 ? (
                  <EmptyState message="Sin ventas en el período" />
                ) : viewTopProductos === 'chart' ? (
                  <div style={{ width: '100%', height: Math.max(240, Math.min(rentabilidadProductos.length, 12) * 34), minWidth: 0 }}>
                    <Chart>
                      <BarChart
                        data={rentabilidadProductos.slice(0, 12).reverse().map((p) => ({
                          name: p.nombre.length > 18 ? p.nombre.slice(0, 18) + '…' : p.nombre,
                          venta: p.venta,
                          beneficio: p.beneficio,
                        }))}
                        layout="vertical"
                        margin={{ top: 0, right: 16, bottom: 0, left: 4 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
                        <XAxis type="number" stroke="#94A3B8" fontSize={10} tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : `${v}`)} />
                        <YAxis type="category" dataKey="name" stroke="#94A3B8" fontSize={10} width={110} tick={{ fill: '#475569' }} />
                        <Tooltip
                          formatter={(value, name) => [`$ ${formatMoney(value)}`, name === 'venta' ? 'Venta' : 'Beneficio']}
                          contentStyle={{ borderRadius: 12, border: '1px solid #E2E8F0', fontSize: 12 }}
                        />
                        <Legend iconSize={8} formatter={(v) => <span style={{ fontSize: 10, color: '#475569' }}>{v === 'venta' ? 'Venta' : 'Beneficio'}</span>} />
                        <Bar dataKey="venta" fill="#51A7FD" radius={[0, 4, 4, 0]} maxBarSize={18} />
                        <Bar dataKey="beneficio" fill="#3FF2AA" radius={[0, 4, 4, 0]} maxBarSize={18} />
                      </BarChart>
                    </Chart>
                  </div>
                ) : (
                  <DataTable
                    emptyMessage="Sin ventas en el período"
                    rows={rentabilidadProductos}
                    columns={[
                      { key: 'nombre', label: 'Producto', cellClass: () => 'font-black text-slate-800 truncate max-w-[200px]' },
                      { key: 'cant', label: 'Unid.', align: 'right', cellClass: () => 'font-bold text-slate-600', render: (p) => formatInt(p.cant) },
                      { key: 'venta', label: 'Venta', align: 'right', cellClass: () => 'font-black text-slate-900', render: (p) => `$ ${formatMoney(p.venta)}` },
                      { key: 'cmv', label: 'Costo', align: 'right', cellClass: () => 'text-slate-500', render: (p) => `$ ${formatMoney(p.cmv)}` },
                      { key: 'beneficio', label: 'Beneficio', align: 'right', cellClass: (p) => `font-black ${p.beneficio < 0 ? 'text-rose-600' : 'text-emerald-600'}`, render: (p) => `$ ${formatMoney(p.beneficio)}` },
                      { key: 'margenPct', label: 'Margen', align: 'right', cellClass: (p) => `font-black ${margenColorClass(p.margenPct)}`, render: (p) => formatPercent(p.margenPct) },
                    ]}
                  />
                )}
              </SectionCard>

              {/* Ventas por categoría / rubro */}
              <SectionCard
                title="Ventas por categoría"
                icon={Layers}
                action={
                  ventasPorCategoria.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setViewCategorias((v) => (v === 'chart' ? 'table' : 'chart'))}
                      title={viewCategorias === 'chart' ? 'Ver tabla' : 'Ver gráfico'}
                      className={`inline-flex h-7 items-center gap-1 rounded-lg border px-2 text-[10px] font-black uppercase tracking-widest transition-colors ${
                        viewCategorias === 'table' ? 'border-blue-200 bg-blue-50 text-blue-600' : 'border-slate-200 bg-white text-slate-400 hover:text-slate-700'
                      }`}
                    >
                      {viewCategorias === 'chart' ? <LayoutList size={12} /> : <BarChart3 size={12} />}
                      {viewCategorias === 'chart' ? 'Tabla' : 'Gráfico'}
                    </button>
                  )
                }
              >
                {ventasPorCategoria.length === 0 ? (
                  <EmptyState icon={Layers} message="Sin ventas en el período" />
                ) : viewCategorias === 'chart' ? (
                  <div style={{ width: '100%', height: 280, minWidth: 0, minHeight: 0 }}>
                    <Chart>
                      <PieChart>
                        <Pie data={ventasPorCategoria} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={95} paddingAngle={2}>
                          {ventasPorCategoria.map((entry, index) => (
                            <Cell key={entry.name} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => `$ ${formatMoney(value)}`} contentStyle={{ borderRadius: 12, border: '1px solid #E2E8F0', fontSize: 12 }} />
                        <Legend verticalAlign="bottom" height={36} iconSize={10} formatter={(value) => <span style={{ fontSize: 11, color: '#475569' }}>{value}</span>} />
                      </PieChart>
                    </Chart>
                  </div>
                ) : (
                  <DataTable
                    emptyMessage="Sin ventas en el período"
                    rows={ventasPorCategoria.map((c) => ({ ...c, id: c.name }))}
                    columns={[
                      { key: 'name', label: 'Categoría', cellClass: () => 'font-black text-slate-800 truncate max-w-[200px]' },
                      { key: 'value', label: 'Venta', align: 'right', cellClass: () => 'font-black text-slate-900', render: (c) => `$ ${formatMoney(c.value)}` },
                      { key: 'pct', label: '%', align: 'right', cellClass: () => 'text-slate-500', render: (c) => formatPercent(c.pct) },
                      { key: 'beneficio', label: 'Beneficio', align: 'right', cellClass: (c) => `font-black ${c.beneficio < 0 ? 'text-rose-600' : 'text-emerald-600'}`, render: (c) => `$ ${formatMoney(c.beneficio)}` },
                      { key: 'margenPct', label: 'Margen', align: 'right', cellClass: (c) => `font-black ${margenColorClass(c.margenPct)}`, render: (c) => formatPercent(c.margenPct) },
                    ]}
                  />
                )}
              </SectionCard>
            </>
  );
}
